import moment from 'moment-timezone';
import React, { useCallback, useContext, useEffect, useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { storeSessionPage } from '../services/Metrics';

import { GroupsImagesContext } from './GroupsImagesRepository';

import { getLocalTimeZone, getQueryStringParams } from '../utils/utils';
import { PACK_BASIC_EXTRA_ID, PACK_BASIC_EXTRAS } from '../utils/packs-utils';
import { session } from '../load-config';

export const CORPORATE_TYPE_ID = {
  ASSISTENCE: 1,
  EMPRESAS_DIRECTO: 2,
  EMPRESAS_FATURACAO: 3,
  GESTORA_FROTA: 4,
}

export const MINIMUM_AGE_FOR_YOUNG_DRIVER = 18;
export const MINIMUM_AGE = 21
export const SPECIAL_GROUP_MINIMUM_AGE = 25

export function getDriversFirstName(name) {
  return name && name.split(',').reverse()[0]
}

export function mapDriversPhone(phone, countryPhoneCode) {
  return phone ? phone.replace(' ', '').trim() === `+${countryPhoneCode}` ? '' : phone.replace(' ', '').trim() : ''
}

export const ALWAYS_PRESENT = ['TP', 'CDW']

/**
 * Grupos de produtos que determinam a franquia, do mais protetor para o menos protetor (o primeiro
 * grupo com presenca ganha). Dentro de cada grupo, o primeiro elemento e o codigo base.
 */
const EXCESS_GROUPS = [['IF', 'IF1', 'IF2', 'IF3'], ['RF', 'RF1', 'RF2'], ['CDW', 'PE']]

export function mapDocumentTypeToLabel(id) {
  switch (id) {
    case 2: return 'passport'
    case 1: return 'idCard'
    default:
      return ''
  }
}

export function mapDocumentType(value) {
  switch (value) {
    case 'Passport':
      return 2;
    case 'Identity card':
      return 1;
    case 1:
    case '1':
      return 1  
    case 2:
    case '2':
      return 2  
    default:
      return 1;
  }
}

export const DomainContext = React.createContext({
  kioskCurrentStationId: '',
  appVersionId: '',
  reservation: {},
  setReservation: () => { },
  setCorporateExtrasFlow: () => { },
  reservationExtras: [],
  setReservationExtras: () => { },
  selectedExtras: [],
  addExtra: () => { },
  removeExtra: () => { },
  getQuantityOfExtra: () => { },
  rentalAgreementQuoteValue: '',
  availableUpsellings: [],
  upselling: {},
  setUpselling: () => { },
  removeUpselling: () => { },
  driverInfo: {},
  setDriverInfo: () => { },
  addDriverInfo: () => { },
  additionalDriverInfo: {},
  addAdditionalDriverInfo: () => { },
  hasAdditionalDriver: () => { },
  addAdditionalDriver: () => { },
  getCountries: () => { },
  setCountries: () => { },
  getComfortExtras: () => { },
  isExtraIncludedInSelectedPack: () => false,
  calculateExtrasListDayValue: () => 0,
  getAuth1Value: () => ({ isEnabled: false, value: 0, withoutDiscountValue: null }),
  getBabySeatExtras: () => { },
  getVehicleForRA: () => { },
  getRAPickUpAndDropOffDate: () => { },
  getDriversCountryName: () => { },
  getDriversCountryISOCode: () => { },
  getDriversPhoneCountryCode: () => { },
  clearAll: () => { },
  rentalAgreementCreation: {},
  setRentalAgreementCreation: () => { },
  excess: '',
  wasDriverInfoVerified: undefined,
  setWasDriverInfoVerified: () => { },
  wasAdditionalDriverInfoVerified: undefined,
  setWasAdditionalDriverInfoVerified: () => { },
  isEligibleForYoungDriver: () => { },
  getDriversMinimumAgeForReservation: () => { },
  addYoungDriver: () => { },
  removeYoungDriver: () => { },
  getUserQuestions: () => { },
  storeUserAnswer: () => { },
  userAnswers: [],
  language: '',
  setLanguage: () => { },
  driverInfoEditMode: false,
  setDriverInfoEditMode: () => { },
  setDriverInfoHasClickedBack: () => { },
  driverInfoHasClickedBack: false,
  setAppFlowConfigs: () => { },
  appFlowConfigs: null,
  setStationsConfigurations: () => { },
  stationsConfigurations: [],
  addVisitedRoutes: () => {},
  visitedRoutes: [],
});

function DomainRepository({ children, ...props }) {
  const [kioskCurrentStationId, innerSetKioskCurrentStationId] = useState(
    localStorage.getItem('kioskCurrentStationId') ? JSON.parse(localStorage.getItem('kioskCurrentStationId')).stationId : '23'
  );
  const [stationsConfigurations, innerSetStationsConfigurations] = useState(
    localStorage.getItem('stationsConfigurations') ? JSON.parse(localStorage.getItem('stationsConfigurations')) : []
  );
  const [pickupDate, innerSetPickupDate] = useState(
    localStorage.getItem('pickupDate') || ''
  );
  const [kioskCurrentPOSId, innerSetKioskCurrentPOSId] = useState(
    localStorage.getItem('kioskCurrentPOSId') ? JSON.parse(localStorage.getItem('kioskCurrentPOSId')).posId : 1
  );
  const [appVersionId, innerSetAppVersionId] = useState(
    localStorage.getItem('appVersionId') ? JSON.parse(localStorage.getItem('appVersionId')).appVersionId : 2
  );
  const [reservationExtras, innerSetReservationExtras] = useState(
    JSON.parse(localStorage.getItem('rentalAgreementExtras'))
  );
  const [reservation, innerSetReservation] = useState(
    JSON.parse(localStorage.getItem('reservation'))
  );
  const [corporateExtrasFlow, innerSetCorporateExtrasFlow] = useState(
    JSON.parse(localStorage.getItem('corporateExtrasFlow'))
  );
  const [appFlowConfigs, innerSetAppFlowConfigs] = useState(
    JSON.parse(localStorage.getItem('appFlowConfigs'))
  );
  const [isCorporate, innerSetIsCorporate] = useState(
    JSON.parse(localStorage.getItem('isCorporate'))
  );
  const [availableUpsellings, innerSetAvailableUpsellings] = useState(
    JSON.parse(localStorage.getItem('availableUpsellings'))
  );
  const [driverInfo, innerSetDriverInfo] = useState(
    JSON.parse(localStorage.getItem('driverInfo')) || {}
  );
  const [additionalDriverInfo, innerSetAdditionalDriverInfo] = useState(
    JSON.parse(localStorage.getItem('additionalDriverInfo')) || {}
  );
  const [rentalAgreementQuoteValue, innerSetRentalAgreementQuoteValue] = useState(
    JSON.parse(localStorage.getItem('rentalAgreementQuoteValue')) || 0
  );
  const [countries, innerSetCountries] = useState(
    JSON.parse(localStorage.getItem('countries')) || []
  );
  const [upselling, innerSetUpselling] = useState(
    JSON.parse(localStorage.getItem('upselling')) || {}
  );
  const [selectedExtras, innerSetSelectedExtras] = useState(
    JSON.parse(localStorage.getItem('selectedExtras')) || []
  );
  const [excess, innerSetExcess] = useState(
    JSON.parse(localStorage.getItem('excess'))
  );

  /**
   * GKIOSK-1889 - o cliente ja passou pela pagina de packs nova e decidiu sobre o Pack Basico (PE).
   *
   * No flow novo o PE e vendido na propria pagina de packs, pelo que a ausencia do extra na selecao
   * so pode ser lida como recusa a partir daqui (ver canDisplayExtra).
   */
  const [packBasicDecided, innerSetPackBasicDecided] = useState(
    JSON.parse(localStorage.getItem('packBasicDecided')) || false
  );

  const [rentalAgreementCreation, innerSetRentalAgreementCreation] = useState(
    JSON.parse(localStorage.getItem('rentalAgreementCreation')) || {}
  );

  const [wasDriverInfoVerified, setWasDriverInfoVerified] = useState(false);
  const [wasAdditionalDriverInfoVerified, setWasAdditionalDriverInfoVerified] = useState(false);
  const [policyAndTermsVerified, setPolicyAndTermsVerified] = useState(false);
  const [additionalDriverPolicyAndTermsVerified, setAdditionalDriverPolicyAndTermsVerified] = useState(false);

  const [userAnswers, setUserAnswers] = useState(
    JSON.parse(localStorage.getItem('userAnswers')) || {}
  );

  const [language, innerSetLanguage] = useState(
    localStorage.getItem('language') ? JSON.parse(localStorage.getItem('language')).lang : 'pt'
  );

  const [driverInfoEditMode, innerSetDriverInfoEditMode] = useState(
    JSON.parse(localStorage.getItem('driverInfoEditMode')) || false
  );

  const [driverInfoHasClickedBack, setDriverInfoHasClickedBack] = useState(
    JSON.parse(localStorage.getItem('driverInfoHasClickedBack')) || false
  );

  const [insuranceSelectedExtras, innerSetInsuranceSelectedExtras] = useState(
    JSON.parse(localStorage.getItem('insuranceSelectedExtras')) || []
  );

  const [removedExtrasInReview, innerSetRemovedExtrasInReview] = useState(
    JSON.parse(localStorage.getItem('removedExtrasInReview')) || []
  );

  const [groupsDetails, innerSetGroupsDetails] = useState(
    JSON.parse(localStorage.getItem('groupsDetails')) || []
  );

  const [sessionId, innerSetSessionId] = useState(
    localStorage.getItem('sessionId')
  );

  const [driverPhoneValidated, innerSetDriverPhoneValidated] = useState(
    JSON.parse(localStorage.getItem('driverPhoneValidated')) || null
  );

  const [notifyResult, innerSetNotifyUserResult] = useState(
    JSON.parse(localStorage.getItem('notifyResult')) || null
  );

  const [blockedTaxNumbersList, innerSetBlockedTaxNumbersList] = useState(
    JSON.parse(localStorage.getItem('blockedTaxNumbersList')) || []
  );

  const [insuranceSelectedPack, innerSetInsuranceSelectedPack] = useState(
    JSON.parse(localStorage.getItem('insuranceSelectedPack')) || null
  );

  const [invalidAddressAlert, innerSetInvalidAddressAlert] = useState(
    localStorage.getItem('invalidAddressAlert') || null
  );

  const [localizations, innerSetLocalizations] = useState(
    JSON.parse(localStorage.getItem('localizations')) || []
  )

  const [localizationAssets, innerSetLocalizationAssets] = useState(
    JSON.parse(localStorage.getItem('localizationAssets')) || {}
  )

  const [pages, innerSetPages] = useState(
    JSON.parse(localStorage.getItem('pages')) || {}
  )

  const [currentPageIndex, innerSetCurrentPageIndex] = useState(
    Number(localStorage.getItem('currentPageIndex')) || -1
  )

  const [invalidBirthdates, innerSetInvalidBirthdates] = useState(
    JSON.parse(localStorage.getItem('invalidBirthdates')) || []
  )

  const [sessionExtras, innerSetSessionExtras] = useState(
    JSON.parse(sessionStorage.getItem('sessionExtras')) || []
  )

  const [metricsParams, innerSetMetricsParams] = useState(
    sessionStorage.getItem('metricsParams') ? JSON.parse(sessionStorage.getItem('metricsParams')) : undefined
  );

  useEffect(() => {
    const queryStringValues = getQueryStringParams(
      window.location.search
    )
    innerSetMetricsParams(queryStringValues)
    sessionStorage.setItem('metricsParams', JSON.stringify(queryStringValues))
  }, [])

  const { groupsConfigurations } = useContext(GroupsImagesContext);

  const { t } = useTranslation();

  function setKioskCurrentStationId(stationId) {
    localStorage.setItem('kioskCurrentStationId', JSON.stringify({ stationId }));
    innerSetKioskCurrentStationId(stationId)
  }

  function setPickupDate(date) {
    localStorage.setItem('pickupDate', date);
    innerSetPickupDate(date)
  }

  function setKioskCurrentPOSId(posId) {
    localStorage.setItem('kioskCurrentPOSId', JSON.stringify({ posId }));
    innerSetKioskCurrentPOSId(posId)
  }

  function setKioskAppVersionId(appVersionId) {
    localStorage.setItem('appVersionId', JSON.stringify({ appVersionId }));
    innerSetAppVersionId(appVersionId)
  }

  function setReservation(reservation) {
    const sessionId = session.getSessionId()
    clearAll()
    session.setSessionId(sessionId)
    
    localStorage.setItem('resNumber', reservation.Reservation)
    localStorage.setItem('reservation', JSON.stringify(reservation));
    innerSetReservation(reservation);
    setExtrasInReservation(reservation);
    setRentalAgreementQuoteValue(reservation.raQuote.cashierAmountDue);
    setAvailableUpsellings(reservation.availableUpsellings, reservation);
  }

  function setCorporateExtrasFlow(config) {
    const _isCorporate = config !== null
    localStorage.setItem('corporateExtrasFlow', JSON.stringify(config));
    localStorage.setItem('isCorporate', _isCorporate);
    innerSetCorporateExtrasFlow(config)
    innerSetIsCorporate(_isCorporate)
  }

  function setRentalAgreementQuoteValue(value) {
    const valueAsNumber = !value || Number(value) < 0 ? Number(0) : Number(value);
    const strValue = valueAsNumber.toFixed(2);
    localStorage.setItem(
      'rentalAgreementQuoteValue',
      JSON.stringify(strValue)
    );
    innerSetRentalAgreementQuoteValue(strValue);
  }

  function setRentalAgreementQuoteValueV2(valueToAdd, valueToRemove = 0) {
    innerSetRentalAgreementQuoteValue(v => {
      const newValue = Number(v) - Number(valueToRemove) + Number(valueToAdd)
      const valueAsNumber = newValue < 0 ? 0 : newValue;
      const strValue = valueAsNumber.toFixed(2);
      sessionStorage.setItem('rentalAgreementQuoteValue', JSON.stringify(strValue));
      return strValue
    });
  }

  function setExtrasInReservation(reservation) {
    const extrasInReservation = reservation.Extras.split(",").map(
      extra => reservation.availableExtras.find(e => e.extraID === extra)
    );
    setReservationExtras(extrasInReservation);
  }

  function setReservationExtras(rentalAgreementExtras) {
    localStorage.setItem(
      'rentalAgreementExtras',
      JSON.stringify(rentalAgreementExtras)
    );
    innerSetReservationExtras(rentalAgreementExtras);
  }

  function setAvailableUpsellings(availableUpsellings, res = reservation) {
    const modAvailableUpsellings = availableUpsellings
      .filter(u => groupsConfigurations[u.groupID])
      .map(u => ({
        ...u,
        dayValueWithTax: roundToTwoDecimalPlaces(
          u.previewValue / res.raQuote.nrDays
        ).toFixed(2)
      }))
      .sort((u1, u2) => Number(u1.previewValue) - Number(u2.previewValue))
      .filter(u => u.groupID !== '01' && u.groupID !== 'P') // Groups not upsold - Set by Guerin 

    localStorage.setItem(
      'availableUpsellings',
      JSON.stringify(modAvailableUpsellings)
    );
    innerSetAvailableUpsellings(modAvailableUpsellings);
  }

  function setDriverInfo(listReservation, _driverInfo = {}) {
    const curDriverInfo = {
      fullName: `${listReservation.DriverShortName || _driverInfo.Driver_Short_Name || ''} ${listReservation.DriverSurname || _driverInfo.Driver_Surname || ''}`,
      email: listReservation.DriverEmail || _driverInfo.EMail,
      emailConfirm: '',
      countryID:
        listReservation.DriversCountryID == 0 ? _driverInfo.Country : listReservation.DriversCountryID || _driverInfo.Country,
      address: listReservation.DriverAddress || _driverInfo.Address,
      doorNumber: listReservation.DriverDoorNumber || '',
      postalCode: listReservation.DriversPostalCode || _driverInfo.Postal_Code,
      city: listReservation.Drivers_City || _driverInfo.City,
      phone: listReservation.DriverMobile || _driverInfo.Mobile_Number,
      taxNumber: listReservation.DriversTaxNumber || _driverInfo.Tax_Number,
      documentID: listReservation.DocumentNumber || _driverInfo.Document_Number,
      documentType: mapDocumentType(listReservation.Document || _driverInfo.Document_Type),
      iDIssuedPlace: listReservation.DocumentIssuePlace || _driverInfo.Document_Place,
      iDExpireDate: listReservation.DocumentDate || _driverInfo.Document_Date,
      birthDate: listReservation.DriverBirth || _driverInfo.Date_of_Birth,
      driversLicense:
        listReservation.DriverLicense == 0 ? _driverInfo.License_Number || '' : listReservation.DriverLicense || _driverInfo.License_Number || '',
      driversLicenseIssuedDate: listReservation.DriverLicenseDate || _driverInfo.License_Date,
      driversLicenseExpirationDate: listReservation.DriverLicenseExpirationDate || _driverInfo.DriverLicenseExpirationDate,
      driversLicenseIssuedPlace: listReservation.DriverLicensePlace || _driverInfo.License_Place
    };
    addDriverInfo(curDriverInfo);
  }

  function setAdditionalDriverInfo(info) {

    const mappedInfo = {
      fullName: info.DriverAddName || '',
      birthDate: info.DriverAddBirth || '',
      driversLicenseIssuedPlace: info.DriverAddLicensePlace || '',
      driversLicense: info.DriverAddLicense || '',
      driversLicenseIssuedDate: info.DriverAddLicenseDate || '',
      driversLicenseExpirationDate: info.DriverAddLicenseExpirationDate || ''
    }
    addAdditionalDriverInfo(mappedInfo)
  }

  function hasAdditionalDriver() {
    return (
      reservation.Extras.includes('ADD1') ||
      selectedExtras.find(e => e.extraID === 'ADD1')
    );
  }

  function getExcessForExtra(extraId) {
    const extra = reservation.availableExtras.find(e => e.extraID === extraId)
    return extra ? Number(extra.excess) : null
  }

  /**
   * GKIOSK-1888 - extras da lista que podem responder pela franquia de `group`, do mais especifico
   * para o menos: primeiro os produtos do proprio grupo, depois os packs que os incluem (um pack
   * cujo codigo nao esta no grupo - ESSI/PPI/SPI, ESSE/PPE/SPE - resolve pelo RF/IF que traz, em vez
   * de cair no CDW) e por fim o codigo base do grupo.
   */
  function excessCandidatesForGroup(group, extraIds) {
    const allPacks = (reservation && reservation.packs) || []
    const packsInList = extraIds.map(id => allPacks.find(p => p.extraId === id)).filter(Boolean)

    const direct = extraIds.filter(id => group.includes(id))
    const viaPack = packsInList.filter(p => (p.extrasList || []).some(e => group.includes(e))).map(p => p.extraId)

    const present = [...new Set([...direct, ...viaPack])]
    return present.length === 0 ? [] : [...new Set([...present, group[0]])]
  }

  function getExcessForExtras(extras) {
    const _extras = extras.filter(Boolean).map(e => e.extraID)

    for (const group of EXCESS_GROUPS) {
      // O codigo base ('RF'/'IF') nem sempre vem na lista de extras do RentWay: ha tarifas/estacoes
      // que so devolvem os packs (RF1/RF2/IF1...). Nesses casos o lookup do codigo base dava null e o
      // UI mostrava "0,00"/"Sem franquia" em vez do valor real (ex.: RF1 e RF2 com excess 650). Por
      // isso tenta-se primeiro o extra concreto que esta presente e so depois o codigo base.
      for (const id of excessCandidatesForGroup(group, _extras)) {
        const value = getExcessForExtra(id)
        if (value !== null) return value
      }
    }

    return reservation.isExclusiveReservation ? reservation.excessNoPriceReduction : getExcessForExtra('CDW')
  }

  function getCountries() {
    return countries.filter(c => c.countryID && !c.name.startsWith('country_'))
  }

  function setCountries(countries) {
    localStorage.setItem('countries', JSON.stringify(countries));
    innerSetCountries(countries);
  }

  function addDriverInfo(additionalDriverInfo) {
    const curDriverInfo = { ...driverInfo, ...additionalDriverInfo };
    localStorage.setItem('driverInfo', JSON.stringify(curDriverInfo));
    innerSetDriverInfo(curDriverInfo);
  }

  function addAdditionalDriverInfo(additionalInfo) {
    const curAdditionalDriverInfo = {
      ...additionalDriverInfo,
      ...additionalInfo
    };
    localStorage.setItem(
      'additionalDriverInfo',
      JSON.stringify(curAdditionalDriverInfo)
    );
    innerSetAdditionalDriverInfo(curAdditionalDriverInfo);
  }

  function setUpselling(newUpselling) {
    let raQuotePrice = Number(rentalAgreementQuoteValue);
    if (upselling.groupID) {
      raQuotePrice -= Number(upselling.previewValue);
    }
    setRentalAgreementQuoteValue(
      raQuotePrice + Number(newUpselling.previewValue)
    );
    localStorage.setItem('upselling', JSON.stringify(newUpselling));
    innerSetUpselling(newUpselling);
  }

  function removeUpselling() {
    if (!upselling.groupID) {
      return;
    }
    setRentalAgreementQuoteValue(
      Number(rentalAgreementQuoteValue) - Number(upselling.previewValue)
    );
    localStorage.setItem('upselling', JSON.stringify({}));
    innerSetUpselling({});
  }

  function addExtra(extra) {
    if (isBabySeatExtra(extra) && !candAddBabySeat(extra)) return

    setRentalAgreementQuoteValue(
      Number(rentalAgreementQuoteValue) + Number(extra.totalPrice)
    );

    const updatedSelectedExtras = selectedExtras.concat({ ...extra, isUserRemovable: extra.isUserRemovable === false ? false : true });
    localStorage.setItem(
      'selectedExtras',
      JSON.stringify(updatedSelectedExtras)
    );
    innerSetSelectedExtras(updatedSelectedExtras);

    function isBabySeatExtra(_extra) {
      return !!(getBabySeatExtras().find(e => e.extraID === _extra.extraID))
    }

    function candAddBabySeat(_extra) {
      const selectedBabySeatExtras = selectedExtras.filter(e => isBabySeatExtra(e))
      let seats = Number((groupsDetails.find(g => g.id === reservation.GroupID) || {}).seats) || 0
      if (upselling && upselling.groupID) {
        seats = Number((groupsDetails.find(g => g.id === upselling.groupID) || {}).seats) || 0
      }
      return selectedBabySeatExtras.length + 1 < seats
    }
  }

  function addAdditionalDriver() {
    const additionalDriverExtra = getComfortExtras().find(e => e.id === 'ADD1');
    if (additionalDriverExtra) {
      addExtra(additionalDriverExtra);
    }
  }

  function removeExtra(extra, removedInReview = false) {
    // if the extra removed is part of a insurance pack, remove selected pack
    if (insuranceSelectedPack && insuranceSelectedPack.extraId === extra.extraID) {
      setInsuranceSelectedPack(null)
    }


    const idxOfExtra = selectedExtras.findIndex(
      e => e.extraID === extra.extraID
    );
    if (idxOfExtra === -1) {
      return;
    }
    const extraToRemoveFromSelectedExtras = selectedExtras[idxOfExtra]

    setRentalAgreementQuoteValue(
      Number(rentalAgreementQuoteValue) - Number(extraToRemoveFromSelectedExtras.totalPrice)
    );
    const updatedSelectedExtras = selectedExtras.filter(
      (e, idx) => idx !== idxOfExtra
    );
    localStorage.setItem(
      'selectedExtras',
      JSON.stringify(updatedSelectedExtras)
    );
    innerSetSelectedExtras(updatedSelectedExtras);

    if (removedInReview)
      saveRemovedExtra(extraToRemoveFromSelectedExtras)
  }

  function saveRemovedExtra(extra) {
    const updatedRemovedExtras = removedExtrasInReview.concat({ extraId: extra.extraID, price: extra.totalPrice })
    localStorage.setItem(
      'removedExtrasInReview',
      JSON.stringify(updatedRemovedExtras)
    );
    innerSetRemovedExtrasInReview(updatedRemovedExtras);

  }

  function changeInsurancePack(packToAdd, packToRemove) {
    const extraToRemove = packToRemove ? packToRemove.extraId : null
    const extraToRemoveMapped = reservation.availableExtras.find(e => e.extraID === extraToRemove)
    const priceToRemove = extraToRemoveMapped ? packToRemove.totalPrice : 0

    const extraToAdd = packToAdd ? packToAdd.extraId : null
    const extraToAddMapped = reservation.availableExtras.find(e => e.extraID === extraToAdd)
    const priceToAdd = extraToAddMapped ? packToAdd.totalPrice : 0

    setRentalAgreementQuoteValueV2(priceToAdd, priceToRemove);

    innerSetSelectedExtras(v => {
      const updatedSelectedExtras = v.filter(e => extraToRemove !== e.extraID);
      if (extraToAdd) {
        const extra = {
          extraID: extraToAdd,
          id: extraToAdd,
          name: extraToAdd,
          isPack: true,
          totalPrice: priceToAdd,
          // GKIOSK-1889 - o cartao do Pack Basic (PE) da vertente exclusive passa por aqui e tem de
          // manter o isUserRemovable: false que o PE ja tinha quando era vendido em
          // /exclusive-reservation. Os packs verdadeiros continuam removiveis no review.
          isUserRemovable: packToAdd.isUserRemovable !== false
        };
        updatedSelectedExtras.push(extra);
      }
      localStorage.setItem('selectedExtras', JSON.stringify(updatedSelectedExtras));
      return updatedSelectedExtras
    });
  }

  function isIncompatibleExtra(extra, array) {
    return array.some(e => {
      const extAv = reservation.availableExtras.find(ea => ea.extraID === e)
      if (!extAv) return false
      if (
        (extAv.must_not_include && extAv.must_not_include.split(',').find(mni => mni === extra.extraID) !== undefined) ||
        (extAv.must_include && extAv.must_include.split(',').find(mni => mni === extra.extraID) !== undefined)
      ) {
        return true
      }
      return false
    })
  }

  function isExtraIncompatibleForReservation(id) {
    const incompatibilities = {
      RF: ['IF'],
    }
    const incomp = incompatibilities[id]
    if (!incomp) return false

    return incomp.some((element, index, array) => {
      return [...reservationExtras.map(e => e.extraID)].includes(element)
    })
  }

  function getValidSafetyExtras() {
    const groupId = getVehicleForRA()
    // safety extras can be configured by Group or by exclusive rules by other extras in the reservation
    const extrasIdsByGroup = ['01', '02', '03', '04', '05'].includes(groupId) ? ['TP', 'CDW', 'ACDW1', 'IF', 'PAI', 'ASTOP'] : ['TP', 'CDW', 'ACDW1', 'RF', 'IF', 'PAI', 'ASTOP']

    return extrasIdsByGroup.filter(e => !isExtraIncompatibleForReservation(e))
  }

  function getRAPickUpAndDropOffDate() {
    const twentyFourHoursMilliSeconds = 24 * 60 * 60 * 1000;
    const reservationDurationInMilliSeconds = moment(
      reservation.DropOffDate
    ).diff(reservation.PickupDate);
    const closestTwentyFourHoursInMilliSecondsManyfoldNumberOfDuration =
      Math.ceil(
        reservationDurationInMilliSeconds / twentyFourHoursMilliSeconds
      ) * twentyFourHoursMilliSeconds;
    const realPickupDate = moment()
      .add(2, 'minutes')
      .tz(getLocalTimeZone())
      .format('YYYY-MM-DD HH:mm');
    const durationUsingRealPickUpDateAndReservationDropOffDate = moment(
      reservation.DropOffDate
    ).diff(realPickupDate);
    const realDropOffDate = (function getRealDropOffDate() {
      if (
        closestTwentyFourHoursInMilliSecondsManyfoldNumberOfDuration <
        durationUsingRealPickUpDateAndReservationDropOffDate
      ) {
        return moment(realPickupDate)
          .add(closestTwentyFourHoursInMilliSecondsManyfoldNumberOfDuration)
          .format('ddd D MMM / HH:mm');
      }
      if (
        closestTwentyFourHoursInMilliSecondsManyfoldNumberOfDuration >
        durationUsingRealPickUpDateAndReservationDropOffDate &&
        reservationDurationInMilliSeconds % twentyFourHoursMilliSeconds === 0
      ) {
        return moment(realPickupDate)
          .add(closestTwentyFourHoursInMilliSecondsManyfoldNumberOfDuration)
          .format('ddd D MMM / HH:mm');
      }
      return moment(reservation.DropOffDate)
        .add(2, 'minutes')
        .format('ddd D MMM / HH:mm');
    })();
    return {
      pickUpDate: realPickupDate,
      dropOffDate: realDropOffDate
    };
  }

  function getDriversCountryName() {
    if (driverInfo.countryID) {
      const curCountry = getCountries().find(
        c => driverInfo.countryID === c.countryID
      );
      return curCountry ? curCountry.name : '';
    }
    return '';
  }

  function getDriversCountryISOCode() {
    if (driverInfo.countryID) {
      const curCountry = getCountries().find(
        c => driverInfo.countryID === c.countryID
      );
      return curCountry ? curCountry.ISOCode : '';
    }
    return '';
  }

  function getDriversPhoneCountryCode() {
    if (driverInfo.countryID) {
      const curCountry = getCountries().find(
        c => driverInfo.countryID === c.countryID
      );
      return curCountry ? curCountry.PhoneCountryCode : '';
    }
    return '';
  }

  function isEligibleForYoungDriver(driversBirthDate) {
    const isYoungDriverAvailable = reservation.availableExtras.find(
      extra => extra.extraID === 'YADDD' && extra.available === 'true'
    );
    if (!isYoungDriverAvailable) {
      return false;
    }
    const age = moment().diff(driversBirthDate, 'years');
    if (age < MINIMUM_AGE_FOR_YOUNG_DRIVER || age >= MINIMUM_AGE) {
      return false;
    }

    return (reservation.youngDriverEligibleGroups || []).includes(reservation.GroupID);
  }

  function addYoungDriver() {
    const youngDriverExtra = reservation.availableExtras.find(
      extra => extra.extraID === 'YADDD' && extra.available === 'true'
    );
    if (youngDriverExtra) {
      addExtra({
        ...youngDriverExtra,
        ...computeExtraPrices(youngDriverExtra),
        id: 'YADDD',
        isUserRemovable: false
      });
    }
  }

  function getYoungDriverPrices() {
    const youngDriverExtra = reservation.availableExtras.find(
      extra => extra.extraID === 'YADDD' && extra.available === 'true'
    );
    return computeExtraPrices(youngDriverExtra)
  }

  function removeYoungDriver() {
    const youngDriver = selectedExtras.find(e => e.extraID === 'YADDD');
    if (youngDriver) {
      removeExtra(youngDriver);
    }
  }

  function getDriversMinimumAgeForReservation() {
    return (reservation.youngDriverEligibleGroups || []).includes(reservation.GroupID) ? MINIMUM_AGE_FOR_YOUNG_DRIVER : SPECIAL_GROUP_MINIMUM_AGE;
  }

  function clearAll() {
    innerSetReservationExtras([]);
    innerSetReservation({});
    innerSetAvailableUpsellings([]);
    innerSetDriverInfo({});
    innerSetAdditionalDriverInfo({});
    innerSetRentalAgreementQuoteValue(0);
    innerSetUpselling({});
    innerSetSelectedExtras([]);
    innerSetExcess('');
    innerSetCountries([]);
    innerSetIsCorporate(false);
    innerSetCorporateExtrasFlow([]);
    setWasDriverInfoVerified(false);
    setWasAdditionalDriverInfoVerified(false);
    setPolicyAndTermsVerified(false);
    setAdditionalDriverPolicyAndTermsVerified(false);
    setUserAnswers({});
    setDriverInfoEditMode(false);
    setDriverInfoHasClickedBack(false);
    setInsuranceSelectedExtras([])
    setRentalAgreementCreation(null)
    setSessionId(null);
    innerSetRemovedExtrasInReview([]);
    innerSetGroupsDetails([]);
    setNotifyUserResult(null)
    setAppFlowConfigs(null)
    setPickupDate('')
    setKioskCurrentStationId('')
    setInsuranceSelectedPack(null)
    setCurrentPageIndex(null)
    innerSetSessionExtras([])
    setDriverPhoneValidated(false)
    clearVisitedRoutes()
    sessionStorage.clear()
  }

  function getCorporateExtras(extrasIds = []) {
    const _extras = [
      {
        id: 'GPS',
        name: 'GPS',
        isUserRemovable: true
      },
      {
        id: 'CBF',
        name: 'Cross Border',
        isUserRemovable: true
      },
      {
        id: 'ASTOP',
        name: 'Assistência em Viagem Top',
        isUserRemovable: true
      },
      {
        id: 'ADD1',
        name: 'Additional Driver',
        isUserRemovable: true
      },
      {
        id: 'UPSELLING',
        name: 'UPSELLING',
        internalExtra: true,
      },
      {
        id: 'BABY0',
        name: 'Baby Seat (Grp 0+)',
        isUserRemovable: true
      },
      {
        id: 'BABY1',
        name: 'Baby Seat (Grp 1)',
        isUserRemovable: true
      },
      {
        id: 'BABY2',
        name: 'Baby Seat (Grp 2/3)',
        isUserRemovable: true
      },
      {
        id: 'BSEAT',
        name: 'Booster Seat',
        isUserRemovable: true
      },
      {
        id: 'TP',
        name: 'Theft protection',
        isUserRemovable: true
      },
      {
        id: 'CDW',
        name: 'Damage Management Fee',
        isUserRemovable: true
      },
      {
        id: 'ACDW1',
        name: 'ACDW Plus',
        isUserRemovable: true
      },
      {
        id: 'RF',
        name: 'Excess Reduction',
        isUserRemovable: true
      },
      {
        id: 'IF',
        name: 'SCDW',
        isUserRemovable: true
      },
      {
        id: 'PAI',
        name: 'For driver’s protection and as well for the other passengers',
        isUserRemovable: true
      },
    ]

    return _extras
      .filter(cExtra => extrasIds.length === 0 || extrasIds.includes(cExtra.id))
      .filter(cExtra =>
        cExtra.internalExtra || reservation.availableExtras.find(
          e => e.available === 'true' && e.extraID === cExtra.id
        )
      )
      .filter(cExtra => !reservationExtras.find(e => e.extraID === cExtra.id))
      .map(cExtra => ({
        ...cExtra,
        ...reservation.availableExtras.find(e => e.extraID === cExtra.id)
      }))
      .map(cExtra => ({
        ...cExtra,
        value: cExtra.value ? parseFloat(cExtra.value) : null,
        taxRate: cExtra.taxRate ? parseFloat(cExtra.taxRate) : null
      }))
      .map(cExtra => ({
        ...cExtra,
        ...computeExtraPrices(cExtra)
      }));
  }

  function getComfortExtras(extrasIds = []) {
    const comfortExtras = [
      {
        id: 'GPS',
        name: 'GPS',
        iconName: 'gps.svg',
        isUserRemovable: true
      },
      {
        id: 'CBF',
        name: 'Cross Border',
        iconName: 'cross-border.svg',
        isUserRemovable: true
      },
      {
        id: 'ADD1',
        name: 'Additional Driver',
        iconName: 'additional-driver.svg',
        isUserRemovable: true
      },
      {
        id: 'PE',
        isUserRemovable: false
      },
    ];
    return comfortExtras
      .filter(cExtra => extrasIds.length === 0 || extrasIds.includes(cExtra.id))
      .filter(cExtra =>
        reservation.availableExtras.find(
          e => e.available === 'true' && e.extraID === cExtra.id
        )
      )
      .filter(cExtra => !reservationExtras.find(e => e.extraID === cExtra.id))
      .map(cExtra => ({
        ...cExtra,
        ...reservation.availableExtras.find(e => e.extraID === cExtra.id)
      }))
      .map(cExtra => ({
        ...cExtra,
        value: parseFloat(cExtra.value),
        taxRate: parseFloat(cExtra.taxRate)
      }))
      .map(cExtra => ({
        ...cExtra,
        ...computeExtraPrices(cExtra)
      }));
  }

  function getInsuranceExtras(extrasIds = []) {
    const insuranceExtras = [
      {
        id: 'TP',
        isUserRemovable: true
      },
      {
        id: 'CDW',
        isUserRemovable: true
      },
      {
        id: 'ACDW',
        isUserRemovable: true
      },
      {
        id: 'RF',
        isUserRemovable: true
      },
      {
        id: 'IF',
        isUserRemovable: true
      },
      {
        id: 'PAI',
        isUserRemovable: true
      }
    ];
    return insuranceExtras
      .filter(cExtra => extrasIds.length === 0 || extrasIds.includes(cExtra.id))
      .filter(cExtra =>
        reservation.availableExtras.find(
          e => e.available === 'true' && e.extraID === cExtra.id
        )
      )
      .filter(cExtra => !reservationExtras.find(e => e.extraID === cExtra.id))
      .map(cExtra => ({
        ...cExtra,
        ...reservation.availableExtras.find(e => e.extraID === cExtra.id)
      }))
      .map(cExtra => ({
        ...cExtra,
        value: parseFloat(cExtra.value),
        taxRate: parseFloat(cExtra.taxRate)
      }))
      .map(cExtra => ({
        ...cExtra,
        ...computeExtraPrices(cExtra)
      }));
  }

  function getBabySeatExtras() {
    const babySeatExtras = [
      {
        id: 'BABY0',
        name: 'Baby Seat (Grp 0+)',
        iconName: 'baby-seat.png',
        isUserRemovable: true
      },
      {
        id: 'BABY1',
        name: 'Baby Seat (Grp 1)',
        iconName: 'baby-seat.png',
        isUserRemovable: true
      },
      {
        id: 'BABY2',
        name: 'Baby Seat (Grp 2/3)',
        iconName: 'baby-seat.png',
        isUserRemovable: true
      },
      {
        id: 'BSEAT',
        name: 'Booster Seat',
        iconName: 'booster-seat.png',
        isUserRemovable: true
      }
    ];
    return babySeatExtras
      .filter(cExtra =>
        reservation.availableExtras.find(
          e => e.available === 'true' && e.extraID === cExtra.id
        )
      )
      // .filter(cExtra => !reservationExtras.find(e => e.extraID === cExtra.id))
      .map(cExtra => ({
        ...cExtra,
        ...reservation.availableExtras.find(e => e.extraID === cExtra.id)
      }))
      .map(cExtra => ({
        ...cExtra,
        value: parseFloat(cExtra.value),
        taxRate: parseFloat(cExtra.taxRate)
      }))
      .map(cExtra => ({
        ...cExtra,
        ...computeExtraPrices(cExtra)
      }));
  }

  function getQuantityOfExtra(extra) {
    return selectedExtras.filter(e => e.extraID === extra.extraID).length;
  }

  function computeExtraPrices(extra) {
    const value = extra.value ? parseFloat(extra.value) : 0
    const taxRate = extra.taxRate ? parseFloat(extra.taxRate) : 0

    if (extra.internalExtra) return { totalPrice: null }
    if (extra.extraByDay === 'true') {
      return {
        totalPrice: roundToTwoDecimalPlaces(
          value * reservation.raQuote.nrDays +
          (value * reservation.raQuote.nrDays * taxRate) / 100
        ).toFixed(2),
        pricePerDay: roundToTwoDecimalPlaces(
          value + (value * taxRate) / 100
        ).toFixed(2)
      };
    }
    return {
      totalPrice: roundToTwoDecimalPlaces(
        extra.value + (extra.value * extra.taxRate) / 100
      ).toFixed(2)
    };
  }

  /**
   * GKIOSK-1888 - soma, por dia, do que os produtos de uma lista custariam comprados em separado.
   * Alimenta o valor rasurado dos cartoes de pack.
   */
  function calculateExtrasListDayValue(extras) {
    if (!extras || extras.length === 0) {
      return 0
    }
    return extras.reduce((prev, curr) => {
      const currExtra = reservation.availableExtras.find(e => e.extraID === curr)
      // Um extra ausente de availableExtras (ex.: PACKH) nao pode reiniciar o acumulador.
      if (!currExtra) return prev
      const values = computeExtraPrices(currExtra)
      return currExtra.extraByDay === 'true' ? prev + Number(values.pricePerDay) : prev + Number(values.totalPrice)
    }, 0)
  }

  /**
   * GKIOSK-1888 - caucao do aluguer e o seu desconto de 50% com pack. Porte de
   * kiosk-stand/src/domain-repository/DomainRepository.js:1825.
   *
   * `reservation.paymentsConfigs` passou a vir tambem no payload do /reservation-mobile para esta
   * funcao poder existir aqui.
   */
  function getAuth1Value(currentExtras) {
    const groupId = getVehicleForRA()
    const clientConfigurations = reservation.clientConfigurations
    const originConfigurations = reservation.originConfigurations
    const needsValueDepositPayment = Boolean(reservation.paymentsConfigs && reservation.paymentsConfigs.auth1Enabled)

    const blockPackDiscount = clientConfigurations && clientConfigurations.blockAuth1PackDiscount !== null ? clientConfigurations.blockAuth1PackDiscount
      : originConfigurations && originConfigurations.blockAuth1PackDiscount !== null ? originConfigurations.blockAuth1PackDiscount : false

    const valueSafetyDeposit = (() => {
      if (!needsValueDepositPayment) return 0

      if (clientConfigurations && clientConfigurations.preAuth1Value !== null) {
        return clientConfigurations.preAuth1Value
      }

      if (originConfigurations) {
        const isDirectClient = reservation.rwClient && reservation.rwClient.Direct_Client === 'true'
        if (isDirectClient) {
          return groupsConfigurations[groupId] ? groupsConfigurations[groupId].safetyDeposit : 0
        }
        if (originConfigurations.preAuth1Enable !== null) {
          return originConfigurations.preAuth1Enable ? originConfigurations.preAuth1Value : 0
        }
      }

      if (groupsConfigurations[groupId]) {
        return groupsConfigurations[groupId].safetyDeposit
      }

      return 0
    })()

    const valueCheckedForExtras = (() => {
      const configuredPacks = reservation && reservation.packs ? reservation.packs : []

      const packInReservation = reservationExtras.some(
        e => configuredPacks.find(p => p.extraId === e.extraID) !== undefined
      )

      const packIncludedInReservation = reservation.availableExtras
        .filter(e => e.extraIncluded === 'true')
        .some(e => configuredPacks.find(p => p.extraId === e.extraID) !== undefined)

      const packAcquired = currentExtras.some(
        e => configuredPacks.find(p => p.extraId === e.extraID) !== undefined
      )

      const withPackSelected = !blockPackDiscount && (packInReservation || packIncludedInReservation || packAcquired)

      return {
        value: withPackSelected ? Number(valueSafetyDeposit) / 2 : valueSafetyDeposit,
        oldValue: withPackSelected ? valueSafetyDeposit : null
      }
    })()

    return { isEnabled: needsValueDepositPayment, value: valueCheckedForExtras.value, withoutDiscountValue: valueCheckedForExtras.oldValue }
  }

  function getVehicleForRA() {
    return upselling && upselling.groupID ? upselling.groupID : reservation ? reservation.GroupID : null;
  }

  function setRentalAgreementCreation(raCreation) {
    localStorage.setItem(
      'rentalAgreementCreation',
      JSON.stringify(raCreation)
    );
    innerSetRentalAgreementCreation(raCreation);
  }

  function getUserQuestions() {
    const userQuestions = [
      {
        id: 'babySeat',
        key: 'babySeatQuestion'
      },
    ]
    return userQuestions
  }

  function storeUserAnswer({ questionID, answer }) {
    const newAnswers = { ...userAnswers, [questionID]: answer };
    setUserAnswers(newAnswers);
    localStorage.setItem('userAnswers', JSON.stringify(newAnswers));
  }

  function setLanguage(langKey) {
    innerSetLanguage(langKey)
    localStorage.setItem('language', JSON.stringify({ lang: langKey }))
  }

  const setCurrentPageIndex = useCallback((v, amount = 1) => {
    if (v === null) {
      innerSetCurrentPageIndex(-1)
      localStorage.setItem('currentPageIndex', -1)
    } else if (v === 1) {
      innerSetCurrentPageIndex(vv => vv + amount)
      localStorage.setItem('currentPageIndex', currentPageIndex + amount)
    } else if (v === -1) {
      innerSetCurrentPageIndex(vv => vv - 1)
      localStorage.setItem('currentPageIndex', currentPageIndex - amount)
    }
  }, [currentPageIndex])

  /**
   * GKIOSK-1888 - versao mais recente de canDisplayExtra, para os saltos de pagina.
   *
   * flowNextRoute/flowPreviousRoute estao memoizados pelo indice de pagina, pelo que guardam a
   * canDisplayExtra do render em que se ENTROU na pagina - e essa avalia as incompatibilidades
   * (must_include/must_not_include do RentWay) e os extras ja incluidos no pack contra a selecao
   * antiga. Como o pack e trocado sem mudar de pagina, os saltos continuavam a ser decididos com o
   * pack anterior. `canDisplayExtra` e uma function declaration, logo ja esta definida aqui.
   */
  const canDisplayExtraRef = useRef(canDisplayExtra)
  canDisplayExtraRef.current = canDisplayExtra

  const flowPreviousRoute = useCallback((history) => {
    let skipCount = 1
    let index = currentPageIndex - 1
    do {
      const nextPage = reservation.pathPages[index]

      const canDisplayExtraPage = nextPage && nextPage.extraId ? getCorporateExtras([nextPage.extraId]).filter(canDisplayExtraRef.current) : true
      if (canDisplayExtraPage) {
        setCurrentPageIndex(-1, skipCount)
        history.goBack()
        return undefined
      }
      index--
      skipCount++
    } while (index >= 0)

    setCurrentPageIndex(null)
    history.goBack()
  }, [setCurrentPageIndex])

  const flowNextRoute = useCallback(() => {
    if (currentPageIndex === reservation.pathPages.length - 1) {
      return '/review-final-reservation'
    }
    let index = currentPageIndex + 1
    let skipCount = 1
    do {
      const nextPage = reservation.pathPages[index]

      const canDisplayExtraPage = nextPage && nextPage.extraId ? getCorporateExtras([nextPage.extraId]).some(canDisplayExtraRef.current) : true
      if (canDisplayExtraPage) {
        setCurrentPageIndex(1, skipCount)
        return nextPage.url
      }
      index++
      skipCount++
    } while (index < reservation.pathPages.length)

    return '/review-final-reservation'
  }, [reservation, currentPageIndex, setCurrentPageIndex])

  /*
  const flowPreviousRoute = useCallback((history) => {
    setCurrentPageIndex(-1)
    history.goBack()
  }, [setCurrentPageIndex])

  const flowNextRoute = useCallback((currentUrl) => {
    if (currentPageIndex === reservation.pathPages.length) {
      return '/review-final-reservation'
    }
    const nextPage = reservation.pathPages[currentPageIndex + 1]
    setCurrentPageIndex(1)
    return nextPage.url
  }, [reservation, currentPageIndex, setCurrentPageIndex])
  */

  function setInsuranceSelectedExtras(iExtras) {
    innerSetInsuranceSelectedExtras(iExtras)
    localStorage.setItem('insuranceSelectedExtras', JSON.stringify(iExtras));
  }

  function setSessionId(id) {
    if (!id) {
      innerSetSessionId(null)
      session.setSessionId(null)
    } else {
      innerSetSessionId(id)
      session.setSessionId(id)
    }
  }

  function setPage(pageObj) {
    storeSessionPage(pageObj)
  }

  function setDriverPhoneValidated(value) {
    localStorage.setItem('driverPhoneValidated', value);
    innerSetDriverPhoneValidated(value)
  }

  function setNotifyUserResult(value) {
    localStorage.setItem('notifyResult', JSON.stringify(value));
    innerSetNotifyUserResult(value)
  }

  function setGroupsDetails(groups) {
    localStorage.setItem('groupsDetails', JSON.stringify(groups));
    innerSetGroupsDetails(groups)
  }

  function setAppFlowConfigs(config) {
    const _isCorporate = config ? config.isCorporate : false
    localStorage.setItem('appFlowConfigs', JSON.stringify(config));
    localStorage.setItem('isCorporate', _isCorporate);
    innerSetAppFlowConfigs(config)
    innerSetIsCorporate(_isCorporate)
  }

  function setBlockedTaxNumbersList(list) {
    localStorage.setItem('blockedTaxNumbersList', JSON.stringify(list));
    innerSetBlockedTaxNumbersList(list)
  }

  function mapPriceForPack(pack) {
    let valueToPack = null

    const configuredPacks = (reservation && reservation.packs ? reservation.packs : [])
    const currentExtrasToCheck = [...selectedExtras.map(e => e.extraID), ...reservationExtras.map(e => e.extraID)]
      .filter(e => !insuranceSelectedPack || (insuranceSelectedPack && insuranceSelectedPack.extraId !== e))

    const packWithMatchingCurrentExtras = insuranceSelectedPack ? null : getBestPackForExtras(currentExtrasToCheck, configuredPacks, true)

    if (pack) {
      const extraFromNextPack = reservation.availableExtras.find(e => e.extraID === pack.extraId)
      if (!extraFromNextPack) return null

      if (!packWithMatchingCurrentExtras) {
        valueToPack =
          pack.extrasList
            .filter(e => currentExtrasToCheck.find(c => c === e))
            .reduce((total, curr) => {
              const currExtra = reservation.availableExtras.find(e => e.extraID === curr)
              return total - currExtra.value
            }, extraFromNextPack.value)
      } else {
        const extraFromCurrentPack = reservation.availableExtras.find(e => e.extraID === packWithMatchingCurrentExtras.extraId)
        if (extraFromCurrentPack) {
          valueToPack = extraFromNextPack.value - extraFromCurrentPack.value
        }
      }
    }
    return valueToPack
  }

  function getBestPackForExtras(extras, packs, mapExtras = false) {
    function uniq(a) {
      const seen = {};
      return a.filter(function (item) {
        return seen.hasOwnProperty(item) ? false : (seen[item] = true);
      });
    }

    const extrasMapped = mapExtras ? uniq(extras.flatMap(e => {
      const pack = packs.find(p => p.extraId === e)
      return pack ? pack.extrasList : [e]
    })) : extras

    const matchingPacks = packs
      .map(p => {
        const packExtras = p.extrasList.filter(e => !ALWAYS_PRESENT.includes(e))
        return packExtras.every(e => extrasMapped.find(ec => e === ec))
          ? { pack: p, matches: packExtras.length } : null
      })
      .filter(p => p)
      .sort((obj1, obj2) => obj2.matches - obj1.matches)

    return matchingPacks && matchingPacks.length > 0 ? matchingPacks[0].pack : null
  }

  function setInsuranceSelectedPack(pack) {
    innerSetInsuranceSelectedPack(pack)
    localStorage.setItem('insuranceSelectedPack', JSON.stringify(pack));
  }

  function setPackBasicDecided(value) {
    innerSetPackBasicDecided(value)
    localStorage.setItem('packBasicDecided', JSON.stringify(value));
  }

  /**
   * GKIOSK-1888 - produtos que o pack ja escolhido inclui.
   *
   * Um pack entra na selecao apenas com o seu proprio codigo (ver changeInsurancePack); os produtos
   * que o compoem vivem em `extrasList`. Sem esta expansao, um produto ja incluido no pack - o
   * Crossborder do Super Protecao, a Assistencia TOP do Protecao - voltaria a ser oferecido e
   * cobrado mais adiante no fluxo.
   *
   * A fonte de verdade e insuranceSelectedPack (so pode haver um pack escolhido de cada vez): se se
   * lesse a uniao dos packs presentes em selectedExtras, um pack que por alguma razao nao tivesse
   * sido retirado da lista continuaria a esconder os seus extras depois de o cliente trocar de pack.
   * Sem pack escolhido, expandem-se na mesma os packs que estejam na selecao.
   *
   * GKIOSK-1889 - o Pack Basic (PE) e um extra e nao uma linha de SAFETY_PACK, pelo que a sua
   * composicao nao vem da BD: expande-se pelo mesmo mapa que o cartao usa.
   */
  function extrasIncludedInSelectedPacks() {
    const allPacks = (reservation && reservation.packs) || []

    const extrasListFor = id =>
      id === PACK_BASIC_EXTRA_ID
        ? PACK_BASIC_EXTRAS
        : (allPacks.find(p => p.extraId === id) || {}).extrasList || []

    const selectedPackIds = insuranceSelectedPack
      ? [insuranceSelectedPack.extraId]
      : selectedExtras.map(e => e.extraID)

    return selectedPackIds.filter(Boolean).reduce((included, id) => {
      extrasListFor(id).forEach(e => {
        if (!included.includes(e)) included.push(e)
      })
      return included
    }, [])
  }

  function isExtraIncludedInSelectedPack(extraId) {
    return Boolean(extraId) && extrasIncludedInSelectedPacks().includes(extraId)
  }

  function canDisplayExtra(obj) {
    if (!obj || !obj.extraID) return false
    const extraAvailable = reservation.availableExtras ? reservation.availableExtras.find(
      e => e.available === 'true' && e.extraID === obj.extraID
    ) : null

    if (!extraAvailable) return false

    // GKIOSK-1888: o que o pack selecionado ja inclui nao volta a ser apresentado.
    if (isExtraIncludedInSelectedPack(obj.extraID)) return false

    /**
     * GKIOSK-1889 - no flow novo o Pack Basico e um cartao da propria pagina de packs, pelo que a
     * ausencia do PE na selecao so significa recusa DEPOIS de o cliente ter passado por essa pagina.
     * Antes disso os extras bloqueados tem de continuar disponiveis, senao eram escondidos sem lhe
     * ter sido oferecido o pack. No flow legacy a decisao vinha da /exclusive-reservation, que
     * aparece antes das paginas de extras, e a inferencia direta mantem-se valida.
     */
    const packBasicDecisionTaken = reservation.newSafetyPacksVariant ? packBasicDecided : true

    const rejectedPackBasic = packBasicDecisionTaken && reservation.isExclusiveReservation && getQuantityOfExtra({ extraID: 'PE' }) === 0
    const extraBlockedByRejectedPackBasic = rejectedPackBasic && reservation?.exclusiveReservationsConfigurations ?
      reservation.exclusiveReservationsConfigurations.extrasBlocked.includes(obj.extraID) : false
    if (extraBlockedByRejectedPackBasic) {
      return false
    }

    const presentInReservation = (reservationExtras.find(e => e.extraID === obj.extraID)) !== undefined

    const isIncompatible = isIncompatibleExtra(obj, [...selectedExtras.map(e => e.extraID), ...reservationExtras.map(e => e.extraID)])

    return !presentInReservation && !isIncompatible
  }

  function mapExtraPrice(obj) {
    if (!obj || !obj.extraID) return null

    const extra = reservation.availableExtras.find(e => e.extraID === obj.extraID)
    if (!extra) return null

    const mappedExtraNumbers = {
      ...extra,
      ...obj,
      value: Number(extra.value),
      taxRate: Number(extra.taxRate)
    }

    // check if by adding this extra, it matches an existing pack and change the value according to the diference of packs
    const configuredPacks = (reservation && reservation.packs ? reservation.packs : [])

    const currentExtrasToCheck = [...selectedExtras.map(e => e.extraID), ...reservationExtras.map(e => e.extraID)]
    const packWithMatchingCurrentExtras = getBestPackForExtras(currentExtrasToCheck, configuredPacks)

    const nextExtrasToCheck = [...currentExtrasToCheck, obj.extraID]
    const packWithMatchingNextExtras = getBestPackForExtras(nextExtrasToCheck, configuredPacks)

    let valueToPack = null

    if (canGoInsurancePacksPage() && packWithMatchingNextExtras) {
      const extraFromNextPack = reservation.availableExtras.find(e => e.extraID === packWithMatchingNextExtras.extraId)
      if (!extraFromNextPack) return null

      if (!packWithMatchingCurrentExtras) {
        valueToPack =
          packWithMatchingNextExtras.extrasList
            .filter(e => currentExtrasToCheck.find(c => c === e))
            .reduce((total, curr) => {
              const currExtra = reservation.availableExtras.find(e => e.extraID === curr)
              return total - currExtra.value
            }, extraFromNextPack.value)
      } else {
        const extraFromCurrentPack = reservation.availableExtras.find(e => e.extraID === packWithMatchingCurrentExtras.extraId)
        if (extraFromCurrentPack) {
          valueToPack = extraFromNextPack.value - extraFromCurrentPack.value
        }
      }
    }

    const extraFromSelectedExtras = selectedExtras.find(
      e => e.extraID === extra.extraID
    );
    if (extraFromSelectedExtras) {
      mappedExtraNumbers.value = extraFromSelectedExtras.value
    } else {
      mappedExtraNumbers.value = valueToPack || mappedExtraNumbers.value
    }

    return { ...mappedExtraNumbers, ...computeExtraPrices(mappedExtraNumbers) }
  }

  function setInvalidAddressAlert(message) {
    innerSetInvalidAddressAlert(message)
    localStorage.setItem('invalidAddressAlert', message);
  }

  function setDriverInfoEditMode(value) {
    innerSetDriverInfoEditMode(value)
    localStorage.setItem('driverInfoEditMode', JSON.stringify(value));
  }

  /**
   * Existiam aqui DUAS declaracoes de canGoInsurancePacksPage; a segunda (esta) escondia a primeira,
   * que era codigo morto. Ficou so a que estava efetivamente em uso, para o comportamento nao mudar.
   *
   * Nota: a versao do kiosk-stand (DomainRepository.js:1628) testa antes appFlowConfigs.
   * insurancePacksActive + pack incluido na tarifa. As duas divergem, mas alinha-las muda a CSV de
   * extras enviada ao RentWay em TODAS as reservas mobile (prepareExtras faz short-circuit por este
   * predicado) - precisa de ticket e sign-off de QA proprios.
   */
  function canGoInsurancePacksPage() {
    const allPacks = (reservation && reservation.packs ? reservation.packs : [])
    const packInReservation = getBestPackForExtras(reservationExtras.map(e => e.extraID), allPacks, true)
    return packInReservation === null
  }

  function isDriverCountryPortugal() {
    return getDriversCountryISOCode() === 'PT'
  }

  function getQuantityOfExtraInReservation(extraId) {
    if (reservationExtras.length === 0) return 0
    return reservationExtras.reduce((prev, curr) => {
      return curr.extraID === extraId ? prev + 1 : prev
    }, 0)
  }

  const setLocalizationAssets = useCallback((values) => {
    localStorage.setItem('localizationAssets', JSON.stringify(values));
    innerSetLocalizationAssets(values)
  }, [innerSetLocalizationAssets])

  const setLocalizations = useCallback((values) => {
    localStorage.setItem('localizations', JSON.stringify(values));
    innerSetLocalizations(values)
  }, [innerSetLocalizations])

  const setPages = useCallback((values) => {
    localStorage.setItem('pages', JSON.stringify(values));
    innerSetPages(values)
  }, [])

  const setInvalidBirthdates = useCallback((values) => {
    localStorage.setItem('invalidBirthdates', JSON.stringify(values));
    innerSetInvalidBirthdates(values)
  }, [])

  const saveSessionExtras = useCallback((extras) => {
    const newValues = [...sessionExtras.filter(e => !extras.some(ee => ee.pageId === e.pageId)), ...extras]
    innerSetSessionExtras(newValues)
    sessionStorage.setItem('sessionExtras', JSON.stringify(newValues));
  }, [sessionExtras])

  const saveSessionExtra = useCallback((extraDetails) => {
    const newValues = [...sessionExtras.filter(e => e.pageId !== extraDetails.pageId), extraDetails]
    innerSetSessionExtras(newValues)
    sessionStorage.setItem('sessionExtras', JSON.stringify(newValues));
  }, [sessionExtras])

  const setStationsConfigurations = useCallback((configs) => {
    innerSetStationsConfigurations(configs)
    localStorage.setItem('stationsConfigurations', JSON.stringify(configs))
  }, [])

  const filteredCountries = useMemo(() => {
    return countries.filter(c => c.countryID)
  }, [countries])

  const [visitedRoutes, innerAddVisitedRoutes] = useState(
    JSON.parse(localStorage.getItem('visitedRoutes')) || []
  )
  const addVisitedRoutes = useCallback((path) => {
    if (!visitedRoutes.includes(path)) {
      innerAddVisitedRoutes(v => [...v, path])
      localStorage.setItem('visitedRoutes', JSON.stringify([...visitedRoutes, path]))
    }
  }, [visitedRoutes])

  const clearVisitedRoutes = useCallback(() => {
    innerAddVisitedRoutes([])
    localStorage.removeItem('visitedRoutes')
  }, [])

  return (
    <DomainContext.Provider
      value={{
        kioskCurrentStationId,
        setKioskCurrentStationId,
        setKioskAppVersionId,
        kioskCurrentPOSId,
        setKioskCurrentPOSId,
        appVersionId,
        reservation,
        setReservation,
        setCorporateExtrasFlow,
        reservationExtras,
        setReservationExtras,
        driverInfo,
        setDriverInfo,
        addDriverInfo,
        additionalDriverInfo,
        addAdditionalDriverInfo,
        hasAdditionalDriver,
        addAdditionalDriver,
        rentalAgreementQuoteValue,
        availableUpsellings,
        upselling,
        setUpselling,
        removeUpselling,
        getComfortExtras,
        selectedExtras,
        addExtra,
        removeExtra,
        getQuantityOfExtra,
        getBabySeatExtras,
        getVehicleForRA,
        excess,
        clearAll,
        getRAPickUpAndDropOffDate,
        countries: filteredCountries,
        getCountries,
        setCountries,
        getDriversCountryName,
        getDriversCountryISOCode,
        getDriversPhoneCountryCode,
        rentalAgreementCreation,
        setRentalAgreementCreation,
        wasDriverInfoVerified,
        setWasDriverInfoVerified,
        wasAdditionalDriverInfoVerified,
        setWasAdditionalDriverInfoVerified,
        policyAndTermsVerified,
        setPolicyAndTermsVerified,
        additionalDriverPolicyAndTermsVerified,
        setAdditionalDriverPolicyAndTermsVerified,
        isEligibleForYoungDriver,
        getDriversMinimumAgeForReservation,
        addYoungDriver,
        removeYoungDriver,
        getUserQuestions,
        userAnswers,
        storeUserAnswer,
        getYoungDriverPrices,
        setLanguage,
        language,
        setDriverInfoEditMode,
        driverInfoEditMode,
        driverInfoHasClickedBack,
        setDriverInfoHasClickedBack,
        corporateExtrasFlow,
        isCorporate,
        flowNextRoute,
        getCorporateExtras,
        isIncompatibleExtra,
        getInsuranceExtras,
        getValidSafetyExtras,
        getExcessForExtras,
        computeExtraPrices,
        insuranceSelectedExtras,
        setInsuranceSelectedExtras,
        changeInsurancePack,
        setSessionId,
        sessionId,
        removedExtrasInReview,
        setPage,
        setDriverPhoneValidated,
        driverPhoneValidated,
        setNotifyUserResult,
        notifyResult,
        setGroupsDetails,
        groupsDetails,
        setAppFlowConfigs,
        appFlowConfigs,
        pickupDate,
        setPickupDate,
        setBlockedTaxNumbersList,
        blockedTaxNumbersList,
        getBestPackForExtras,
        mapPriceForPack,
        insuranceSelectedPack,
        setInsuranceSelectedPack,
        packBasicDecided,
        setPackBasicDecided,
        saveRemovedExtra,
        canDisplayExtra,
        isExtraIncludedInSelectedPack,
        calculateExtrasListDayValue,
        getAuth1Value,
        mapExtraPrice,
        setInvalidAddressAlert,
        invalidAddressAlert,
        canGoInsurancePacksPage,
        isDriverCountryPortugal,
        getQuantityOfExtraInReservation,
        getExcessForExtra,
        setLocalizationAssets,
        localizationAssets,
        setPages,
        pages,
        flowPreviousRoute,
        setLocalizations,
        localizations,
        setInvalidBirthdates,
        invalidBirthdates,
        sessionExtras,
        saveSessionExtras,
        saveSessionExtra,
        metricsParams,
        setStationsConfigurations,
        stationsConfigurations,
        addVisitedRoutes,
        visitedRoutes,
        setAdditionalDriverInfo,
      }}
    >
      {children}
    </DomainContext.Provider>
  );
}

function roundToTwoDecimalPlaces(num) {
  return Math.round(num * 100) / 100;
}

export const DomainContextConsumer = (WrappedComponent) => (props) => {

  return (
    <DomainContext.Consumer>
      {context => <WrappedComponent context={context} {...props} />}
    </DomainContext.Consumer>
  )
}

export default DomainRepository;
